import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface InvitePreview {
  listName: string;
  inviterName: string;
  itemCount: number;
  listType: string;
}

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'preview' | 'accepting' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<InvitePreview | null>(null);

  // Step 1: Always load a public preview first
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const response = await api.get(`/invitations/preview/${token}`);
        setPreview(response.data.data);
        setStatus('preview');
      } catch (error: any) {
        setStatus('error');
        setMessage(error.response?.data?.message || 'Einladung nicht gefunden oder abgelaufen.');
      }
    };
    loadPreview();
  }, [token]);

  // Step 2: If user is already logged in and preview loaded, accept directly
  useEffect(() => {
    if (user && status === 'preview') {
      acceptInvite();
    }
  }, [user, status]);

  const acceptInvite = async () => {
    setStatus('accepting');
    try {
      const response = await api.post(`/invitations/accept/${token}`);
      const { listId } = response.data.data;
      localStorage.removeItem('redirectAfterLogin');
      navigate(`/lists/${listId}`);
    } catch (error: any) {
      setStatus('error');
      setMessage(error.response?.data?.message || 'Fehler beim Annehmen der Einladung');
    }
  };

  const goToRegister = () => {
    localStorage.setItem('redirectAfterLogin', `/invite/${token}`);
    navigate('/register');
  };

  const goToLogin = () => {
    localStorage.setItem('redirectAfterLogin', `/invite/${token}`);
    navigate('/login');
  };

  // Loading spinner
  if (status === 'loading' || status === 'accepting') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  // Error
  if (status === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="card" style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>❌</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Einladung ungültig</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>{message}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary" style={{ width: '100%' }}>
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  // Preview: not logged in
  if (status === 'preview' && !user && preview) {
    const listEmoji = preview.listType === 'TODO' ? '✅' : '🛒';
    const itemLabel = preview.listType === 'TODO' ? 'Aufgaben' : 'Artikel';

    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ width: '100%', maxWidth: '420px' }}>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
            borderRadius: '1rem 1rem 0 0',
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--btn-text, #0d1117)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{listEmoji}</div>
            <p style={{ opacity: 0.85, fontSize: '0.9rem', marginBottom: '0.25rem' }}>
              <strong>{preview.inviterName}</strong> hat dich eingeladen
            </p>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', lineHeight: '1.2' }}>
              {preview.listName}
            </h1>
            {preview.itemCount > 0 && (
              <p style={{ marginTop: '0.5rem', opacity: 0.75, fontSize: '0.875rem' }}>
                {preview.itemCount} {itemLabel} bereits auf der Liste
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="card" style={{ borderRadius: '0 0 1rem 1rem', padding: '1.75rem' }}>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem', fontSize: '0.9rem', textAlign: 'center' }}>
              Erstelle kostenlos ein Konto um der Liste beizutreten – dauert 30 Sekunden.
            </p>
            <button
              onClick={goToRegister}
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '0.625rem' }}
            >
              Kostenlos registrieren & beitreten
            </button>
            <button
              onClick={goToLogin}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              Ich habe bereits ein Konto
            </button>
          </div>

        </div>
      </div>
    );
  }

  return null;
}

